import { useEffect, useState } from "react";
import {
  api,
  type CycleHistoryFilters,
  type CycleHistoryPage,
  type PnlSign,
} from "../api/client";
import CycleTable from "../components/history/CycleTable";
import CsvExport from "../components/history/CsvExport";
import AppShell from "../components/layout/AppShell";
import PageOwner from "../components/common/PageOwner";
import Panel from "../components/common/Panel";
import { formatExitReason } from "../components/positions/closeLabels";
import { Button } from "@/components/ui/button";

/**
 * SPEC §14 Historique — LE navigateur de cycles : filtres, pagination, drawer
 * forensic, export CSV. Source : GET /api/cycles/history. Contrat d'états (B5b) :
 * chargement → spinner, erreur → explicite (jamais de lignes périmées), succès →
 * lignes. Les agrégats vivent sur l'Analytique (un fait = une maison — l'ex-panneau
 * « Summary » les dupliquait sous la ligne de flottaison). Filtre ⇒ page 0.
 */
const PAGE_SIZE = 20;
const CONTROL = "field";

const END_REASONS = [
  "USER_STOP",
  "ALL_POSITIONS_STOPPED_OUT",
  "ALL_MANAGED_POSITIONS_CLOSED",
  "MARKET_CLOSED",
] as const;

export default function HistoryPage() {
  const [filters, setFilters] = useState<CycleHistoryFilters>({});
  const [page, setPage] = useState(0);
  const [data, setData] = useState<CycleHistoryPage | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(false);

  useEffect(() => {
    let cancelled = false;
    setLoading(true);
    setError(false);
    api
      .historyPage(page, PAGE_SIZE, filters)
      .then((res) => {
        if (cancelled) return;
        setData(res);
        setLoading(false);
      })
      .catch(() => {
        if (cancelled) return;
        setData(null);
        setError(true);
        setLoading(false);
      });
    return () => {
      cancelled = true;
    };
  }, [filters, page]);

  const updateFilter = <K extends keyof CycleHistoryFilters>(
    key: K,
    value: CycleHistoryFilters[K] | undefined,
  ) => {
    setFilters((prev) => {
      const next: CycleHistoryFilters = { ...prev };
      if (value == null || value === "") {
        delete next[key];
      } else {
        next[key] = value;
      }
      return next;
    });
    setPage(0);
  };

  const rows = data?.content ?? [];
  const totalPages = data?.totalPages ?? 0;

  return (
    <AppShell>
      <div className="stagger flex flex-col gap-4">
        {/* Range-only 2026-09-01 : la vue « Commandes » est retirée avec le ticket libre —
            retour à la règle variante B : un marqueur, pas d'onglets. */}
        <PageOwner variant="auto" />
        <Panel
          title="Filtres"
          aria-label="Filtres"
          aside={
            <CsvExport
              filters={filters}
              disabled={loading || error || rows.length === 0}
            />
          }
        >
          <div className="flex flex-wrap items-center gap-4">
            <label className="text-muted-foreground flex items-center gap-2 text-sm">
              Biais
              <select
                className={CONTROL}
                data-testid="filter-bias"
                value={filters.bias ?? ""}
                onChange={(e) =>
                  updateFilter("bias", (e.target.value as "BUY" | "SELL" | "") || undefined)
                }
              >
                <option value="">Tous</option>
                <option value="BUY">BUY</option>
                <option value="SELL">SELL</option>
              </select>
            </label>
            <label className="text-muted-foreground flex items-center gap-2 text-sm">
              P&amp;L
              <select
                className={CONTROL}
                data-testid="filter-pnlsign"
                value={filters.pnlSign ?? ""}
                onChange={(e) =>
                  updateFilter("pnlSign", (e.target.value as PnlSign | "") || undefined)
                }
              >
                <option value="">Tous</option>
                <option value="POS">Gain</option>
                <option value="NEG">Perte</option>
                <option value="FLAT">Neutre</option>
              </select>
            </label>
            <label className="text-muted-foreground flex items-center gap-2 text-sm">
              Fin de cycle
              <select
                className={CONTROL}
                data-testid="filter-endReason"
                value={filters.endReason ?? ""}
                onChange={(e) => updateFilter("endReason", e.target.value || undefined)}
              >
                <option value="">Toutes</option>
                {END_REASONS.map((r) => (
                  <option key={r} value={r}>
                    {formatExitReason(r)}
                  </option>
                ))}
              </select>
            </label>
            <label className="text-muted-foreground flex items-center gap-2 text-sm">
              Du
              <input
                type="date"
                className={CONTROL}
                data-testid="filter-from"
                value={filters.from?.slice(0, 10) ?? ""}
                onChange={(e) =>
                  updateFilter("from", e.target.value ? `${e.target.value}T00:00:00Z` : undefined)
                }
              />
            </label>
            <label className="text-muted-foreground flex items-center gap-2 text-sm">
              Au
              <input
                type="date"
                className={CONTROL}
                data-testid="filter-to"
                value={filters.to?.slice(0, 10) ?? ""}
                onChange={(e) =>
                  updateFilter("to", e.target.value ? `${e.target.value}T23:59:59Z` : undefined)
                }
              />
            </label>
          </div>
        </Panel>

        <Panel
          title="Cycles"
          aria-label="Cycles"
          aside={
            <span>{error ? "?" : loading ? "…" : `${data?.totalElements ?? 0} cycles`}</span>
          }
          bodyClassName="p-0"
        >
          <div className="p-2">
            <CycleTable rows={rows} loading={loading} error={error} />
          </div>
          {totalPages > 1 && (
            <div className="border-border flex items-center justify-center gap-3 border-t p-3">
              <Button
                type="button"
                size="sm"
                variant="secondary"
                onClick={() => setPage((p) => Math.max(0, p - 1))}
                disabled={page === 0 || loading}
                data-testid="page-prev"
              >
                ‹ Préc.
              </Button>
              <span className="text-muted-foreground text-sm" data-testid="page-indicator">
                Page {page + 1} / {totalPages}
              </span>
              <Button
                type="button"
                size="sm"
                variant="secondary"
                onClick={() => setPage((p) => p + 1)}
                disabled={page >= totalPages - 1 || loading}
                data-testid="page-next"
              >
                Suiv. ›
              </Button>
            </div>
          )}
        </Panel>
      </div>
    </AppShell>
  );
}
